import React from 'react';
import { Phone, Headset } from 'lucide-react';

/**
 * Τηλέφωνο υποστήριξης — μοναδική πηγή αλήθειας. Εμφανίζεται στατικά (όχι
 * κουμπί) στις οθόνες σύνδεσης (login, επιλογή υπαλλήλου, PIN) και στις μπάρες
 * του POS, ώστε ταμίας/διαχειριστής να το βρίσκουν αμέσως όταν το χρειάζονται.
 */
export const SUPPORT_PHONE = '2107005831';
export const SUPPORT_PHONE_DISPLAY = '210 700 5831';

interface SupportContactProps {
  /** 'light' για ανοιχτό/gradient φόντο, 'dark' για σκούρο slate φόντο. */
  tone?: 'light' | 'dark';
  /** 'chip' = μια γραμμή με ετικέτα, 'card' = πιο εμφανές (σφάλματα), 'mini' = για μπάρες. */
  variant?: 'chip' | 'card' | 'mini';
  className?: string;
}

const SupportContact: React.FC<SupportContactProps> = ({ tone = 'dark', variant = 'chip', className = '' }) => {
  const light = tone === 'light';

  // Συμπαγές για μπάρες POS: εικονίδιο + αριθμός, χωρίς τη λέξη «Υποστήριξη».
  if (variant === 'mini') {
    return (
      <span
        title={`Τεχνική υποστήριξη: ${SUPPORT_PHONE_DISPLAY}`}
        className={[
          'inline-flex items-center gap-1.5 text-sm font-semibold select-none',
          light ? 'text-white' : 'text-t2',
          className,
        ].join(' ')}
      >
        <Headset className={`h-4 w-4 shrink-0 ${light ? 'text-white/80' : 'text-cyan-600 dark:text-cyan-400'}`} />
        <span className="hidden sm:inline tracking-wide">{SUPPORT_PHONE_DISPLAY}</span>
      </span>
    );
  }

  // Εμφανές μπλοκ για καταστάσεις σφάλματος («δεν συνδέεται»).
  if (variant === 'card') {
    return (
      <div className={['inline-flex items-center gap-3 select-none', className].join(' ')}>
        <Headset className={`h-6 w-6 shrink-0 ${light ? 'text-white' : 'text-cyan-600 dark:text-cyan-400'}`} />
        <span className="text-left leading-tight">
          <span className={`block text-xs ${light ? 'text-white/70' : 'text-t3'}`}>Τεχνική υποστήριξη</span>
          <span className={`block text-lg font-bold tracking-wide ${light ? 'text-white' : 'text-cyan-700 dark:text-cyan-100'}`}>{SUPPORT_PHONE_DISPLAY}</span>
        </span>
      </div>
    );
  }

  // Μία διακριτική γραμμή: εικονίδιο + «Υποστήριξη» + αριθμός.
  return (
    <span
      className={[
        'inline-flex items-center gap-2 text-sm font-medium select-none',
        light ? 'text-white/90' : 'text-t2',
        className,
      ].join(' ')}
    >
      <Phone className={`h-4 w-4 ${light ? 'text-white/80' : 'text-cyan-600 dark:text-cyan-400'}`} />
      <span className={light ? 'text-white/70' : 'text-t3'}>Υποστήριξη</span>
      <span className="font-semibold tracking-wide">{SUPPORT_PHONE_DISPLAY}</span>
    </span>
  );
};

export default SupportContact;
